import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Play } from 'lucide-react';
import { Button, Card, buttonVariants, cn } from '@wundo/ui';
import {
  PILOT_CONTEXT_KEY,
  fetchPilotContext,
  type PilotPractice,
} from '@wundo/client/pilot.ts';
import { m } from '@/paraglide/messages.js';

/**
 * The last screen of the pilot wizard: the way into the oefenbibliotheek, and a
 * picture of what is waiting there.
 *
 * The picture is the point. "Je oefengesprekken staan klaar" is a claim, and
 * the card beside it is the claim shown — this week's conversation, named, in
 * the shape it has on the page the button leads to.
 */
export const Route = createFileRoute('/onboarding/ready-to-practise')({
  component: OnboardingReadyToPractisePage,
});

export function OnboardingReadyToPractisePage() {
  const navigate = useNavigate();

  const { data: pilot } = useQuery({
    queryKey: PILOT_CONTEXT_KEY,
    queryFn: fetchPilotContext,
  });

  return (
    <Card
      padding='none'
      className='mx-auto flex w-full max-w-225 overflow-hidden max-mobile:flex-col'
    >
      <div className='min-w-0 flex-1 px-9 py-10 max-mobile:px-6 max-mobile:py-8'>
        <p className='mb-3.5 text-xs font-bold tracking-wider text-ink-muted uppercase'>
          {m.onboarding_pilot_ready_kicker()}
        </p>
        <h1 className='mb-3 text-xl font-medium tracking-tight'>
          {m.onboarding_pilot_ready_title()}
        </h1>
        <p className='text-md text-ink-muted'>{m.onboarding_pilot_ready_body()}</p>

        <div className='mt-8 flex items-center gap-3'>
          <Button
            variant='accent'
            shape='block'
            size='lg'
            onClick={() => void navigate({ to: '/practice' })}
          >
            {m.onboarding_pilot_ready_continue()}
            <ArrowRight aria-hidden='true' className='size-4' />
          </Button>
          <Button
            variant='ghost'
            shape='block'
            size='lg'
            onClick={() => void navigate({ to: '/onboarding/how-it-works' })}
          >
            {m.onboarding_back()}
          </Button>
        </div>
      </div>

      <div className='flex min-w-0 flex-1 items-center justify-center border-l border-fill bg-fill-faint p-7.5 max-mobile:border-t max-mobile:border-l-0'>
        <ThisWeekPreview practice={pilot?.practice ?? null} />
      </div>
    </Card>
  );
}

/**
 * The oefenbibliotheek as it will look, in one card. Null is a pilot whose
 * first conversation has not been put there yet: the card stays, because what
 * it is showing is where the conversation will appear, not whether one exists.
 */
function ThisWeekPreview({ practice }: { practice: PilotPractice | null }) {
  return (
    <Card as='section' className='w-full max-w-95'>
      <p className='text-xs font-bold tracking-wider text-ink-muted uppercase'>
        {m.onboarding_pilot_ready_preview_label()}
      </p>
      <h2 className='mt-1.5 text-md font-bold text-ink'>
        {m.onboarding_pilot_ready_preview_heading()}
      </h2>

      {practice === null ? (
        <p className='mt-4 text-sm text-ink-muted'>
          {m.onboarding_pilot_ready_preview_empty()}
        </p>
      ) : (
        <div className='mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line px-4 py-3.5'>
          <div className='min-w-0'>
            <p className='text-base font-bold text-ink'>{practice.title}</p>
            <p className='text-sm text-ink-secondary'>
              {m.onboarding_pilot_ready_preview_subtitle({
                subtitle: practice.subtitle,
              })}
            </p>
          </div>
          {/*
            A picture of the button on the real card, not a button: pressing it
            here would start a practice conversation on top of the wizard.
          */}
          <span
            className={cn(
              buttonVariants({ variant: 'quiet', shape: 'block', size: 'sm' }),
              'cursor-default font-medium',
            )}
          >
            <Play className='size-3.5' aria-hidden='true' />
            {m.onboarding_pilot_ready_preview_action()}
          </span>
        </div>
      )}
    </Card>
  );
}
